import {
  Alert,
  Icon,
  InputControl,
  Radio,
  RadioGroup,
  Tag,
} from "@growtherapy/sprout-ui";
import { useTranslate } from "@growtherapy/i18n";
import { QualityMode } from "../../video-sdk/core/room/participant/media/types";
import { faVideo, faWifiExclamation } from "@fortawesome/pro-solid-svg-icons";
import { consolidateQualityMode } from "../../video-sdk/core/utils";

export interface VideoQualitySettingsProps {
  qualityMode: QualityMode;
  onChangeQualityMode?: (mode: QualityMode) => void;
  shouldDisableFields?: boolean;
  shouldShowAutoOptimizationAlert?: boolean;
  storedQualityMode?: QualityMode;
}

export function VideoQualitySettings({
  qualityMode,
  onChangeQualityMode: changeQualityMode,
  shouldDisableFields,
  shouldShowAutoOptimizationAlert,
  storedQualityMode,
}: VideoQualitySettingsProps) {
  const t = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <InputControl
        label={t("telehealth-core:settings.quality")}
        className="mb-0"
        disabled={shouldDisableFields}
      >
        <RadioGroup
          onChange={(event) =>
            changeQualityMode?.(event.target.value as QualityMode)
          }
          value={consolidateQualityMode(qualityMode)}
          className="pt-5"
          layout="column"
          name={t("telehealth-core:settings.quality")}
        >
          <Radio
            label={t("telehealth-core:settings.optimizeForQuality")}
            value={QualityMode.High}
          />
          <Radio
            label={
              <span className="flex flex-wrap items-center gap-1 sm:gap-2">
                {t("telehealth-core:settings.optimizeForPerformance")}
                <Tag
                  className="inline-flex gap-2 w-content items-center"
                  use="neutral"
                  aria-label={t(
                    "telehealth-core:settings.checkThisBoxIfYouHavePoorConnectivity",
                  )}
                >
                  <Icon icon={faWifiExclamation} aria-hidden />
                  <span>
                    {t("telehealth-core:settings.forLowConnectivity")}
                  </span>
                </Tag>
              </span>
            }
            value={QualityMode.Low}
          />
        </RadioGroup>
      </InputControl>
      {shouldShowAutoOptimizationAlert &&
        // Prevents the alert from showing if the user has already set their
        // own quality mode to low I.e. the quality changes that are reflected
        // back by the remote participant won't result in this alert showing
        storedQualityMode !== QualityMode.Low && (
          <Alert
            className="bg-neutral-200"
            heading={t(
              "telehealth-core:settings.aParticipantHasOptimizedTheSessionVideoForPerformance",
            )}
            icon={faVideo}
            use="info"
          >
            {t("telehealth-core:settings.youMayUpdateThisSettingForYourself")}
          </Alert>
        )}
    </div>
  );
}
